import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ShellClientService } from 'projects/shared-components/shell-communication/shell-client.service';
import { TelegramConnections, TelegramConnectionReply } from 'projects/shared-components/shell-communication/operations/shell/telegram-connections.class';
import * as qrcode from 'qrcode-generator';

@Component({
  selector: 'ets-telegram-connections',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <button type="button" title="Telegram Connections" [attr.aria-label]="linked ? 'Telegram Connections — linked' : 'Telegram Connections — not linked'" (click)="open()"
      class="telegram-launch" [class.is-linked]="linked">
      <i class="fab fa-telegram-plane" aria-hidden="true"></i>
    </button>
    <dx-popup [width]="400" [height]="530" title="Telegram Connections" [showTitle]="true" [visible]="visible"
      [closeOnOutsideClick]="false" (onHidden)="close()">
      <div *dxTemplate="let data of 'content'" class="telegram-dialog-body">
        <ng-container *ngIf="linked; else unlinked">
          <p class="telegram-linked-message">This account is already linked to Telegram</p>
          <div class="telegram-identity"><span class="telegram-caption">Telegram ID</span><span class="telegram-id">{{telegramId}}</span></div>
          <p *ngIf="status" class="telegram-status" role="status">{{status}}</p>
          <dx-button text="Revoke access" (onClick)="revoke()" [disabled]="busy"></dx-button>
        </ng-container>
        <ng-template #unlinked>
          <p *ngIf="qrImage" class="telegram-instruction">Scan this QR code with your phone.<br>Tap Start in Telegram if prompted.</p>
          <img *ngIf="qrImage" [src]="qrImage" alt="Scan to connect Telegram" class="telegram-qr" width="208" height="208">
          <div *ngIf="qrImage" class="telegram-qr-details"><span>Expires in {{countdown}}</span><span class="telegram-caption">Keep this QR code private.</span></div>
          <p *ngIf="!qrImage && status" class="telegram-status" role="status">{{status}}</p>
          <dx-button *ngIf="!busy" text="Generate New QR" (onClick)="create()"></dx-button>
        </ng-template>
      </div>
    </dx-popup>`,
  styles: [`
    :host { display:block; height:100%; }
    .telegram-launch { height:100%; background:transparent; border:0; color:inherit; cursor:pointer; padding:0 14px; font-size:18px; }
    .telegram-launch.is-linked { color:#4caf50; }
    .telegram-dialog-body { height:100%; box-sizing:border-box; padding:12px 16px; display:flex; flex-direction:column; align-items:center; justify-content:center; text-align:center; gap:18px; }
    .telegram-dialog-body p { margin:0; max-width:290px; line-height:1.5; }
    .telegram-instruction, .telegram-status { font-size:14px; }
    .telegram-linked-message { font-size:17px; font-weight:500; }
    .telegram-identity { display:flex; flex-direction:column; gap:7px; margin:3px 0 12px; }
    .telegram-caption { color:#aaa; font-size:12px; }
    .telegram-id { font-size:18px; font-variant-numeric:tabular-nums; }
    .telegram-qr { display:block; background:white; max-width:100%; object-fit:contain; flex-shrink:0; }
    .telegram-qr-details { display:flex; flex-direction:column; gap:3px; font-size:13px; font-variant-numeric:tabular-nums; }
  `]
})
export class TelegramConnectionsComponent implements OnInit, OnDestroy {
  visible = false; busy = false; linked = false; status = ''; qrImage = ''; remaining = 0; requestId?: string;
  telegramId?: number;
  private timer?: ReturnType<typeof setTimeout>;
  private generation = 0;
  private destroyed = false;
  private expires = 0;
  constructor(private shell: ShellClientService, private changes: ChangeDetectorRef) {}
  private readonly focus = () => { if (!this.visible) void this.refreshStatus(); };
  ngOnInit() { void this.refreshStatus(); window.addEventListener('focus', this.focus); }
  get countdown() { return Math.floor(this.remaining / 60) + ':' + ('0' + this.remaining % 60).slice(-2); }
  private async refreshStatus() {
    const version = this.generation;
    try {
      const reply = await this.shell.processQuery<TelegramConnectionReply>(new TelegramConnections('List'));
      if (!this.destroyed && version === this.generation && !this.visible) { this.connected(reply); this.render(); }
    } catch { /* Opening the dialog retries an unavailable status read. */ }
  }
  private render() { if (!this.destroyed) this.changes.markForCheck(); }
  async open() {
    if (this.visible) return;
    this.visible = true; this.status = 'Checking connection…'; this.busy = true;
    const version = ++this.generation;
    try {
      const reply = await this.shell.processQuery<TelegramConnectionReply>(new TelegramConnections('List'));
      if (version !== this.generation) return;
      this.connected(reply);
      if (!this.linked) { this.busy = false; await this.create(); }
    } catch { if (version === this.generation) this.status = 'Connection unavailable. Try again.'; }
    finally { if (version === this.generation) { this.busy = false; this.render(); } }
  }
  private connected(reply: TelegramConnectionReply) {
    this.linked = reply.status === 'Linked';
    this.telegramId = this.linked ? reply.telegramUserId : undefined;
    if (this.linked) {
      this.qrImage = ''; this.requestId = undefined; clearTimeout(this.timer);
      this.status = '';
    }
  }
  async create() {
    if (this.busy) return;
    this.busy = true; clearTimeout(this.timer); this.qrImage = ''; this.status = 'Generating QR…';
    const version = ++this.generation;
    this.render();
    try {
      const reply = await this.shell.processQuery<TelegramConnectionReply>(new TelegramConnections('CreateQr'));
      if (version !== this.generation) {
        if (reply.requestId) void this.cancel(reply.requestId);
        return;
      }
      this.requestId = reply.requestId;
      if (reply.status === 'Pending' && reply.linkUrl && reply.expiresUtc) {
        const code = qrcode(0, 'M'); code.addData(reply.linkUrl); code.make();
        this.qrImage = code.createDataURL(6, 4);
        this.expires = Date.parse(/Z$/i.test(reply.expiresUtc) ? reply.expiresUtc : reply.expiresUtc + 'Z');
        this.remaining = Math.max(0, Math.ceil((this.expires - Date.now()) / 1000));
        this.status = 'Waiting for your scan…'; this.timer = setTimeout(() => this.poll(version), 1000);
      } else if (reply.status === 'AlreadyLinked') {
        const current = await this.shell.processQuery<TelegramConnectionReply>(new TelegramConnections('List'));
        if (version === this.generation) this.connected(current);
      } else this.status = 'Your account or Shell is unavailable.';
    } catch { if (version === this.generation) this.status = 'Could not generate QR. Try again.'; }
    finally { if (version === this.generation) { this.busy = false; this.render(); } }
  }
  private async poll(version: number) {
    if (version !== this.generation || !this.visible || !this.requestId) return;
    this.remaining = Math.max(0, Math.ceil((this.expires - Date.now()) / 1000));
    if (!this.remaining) { this.qrImage = ''; this.status = 'QR expired. Generate a new QR.'; this.render(); return; }
    try {
      const reply = await this.shell.processQuery<TelegramConnectionReply>(new TelegramConnections('QrStatus', this.requestId));
      if (version !== this.generation) return;
      this.connected(reply);
      if (this.linked) { this.render(); return; }
      if (reply.status !== 'Pending') { this.qrImage = ''; this.status = 'QR is no longer valid. Generate a new QR.'; this.render(); return; }
    } catch { if (version === this.generation) this.status = 'Checking connection…'; }
    if (version === this.generation) { this.render(); this.timer = setTimeout(() => this.poll(version), 1000); }
  }
  async revoke() {
    if (this.busy) return;
    this.busy = true; this.render(); const version = ++this.generation;
    try {
      const reply = await this.shell.processQuery<TelegramConnectionReply>(new TelegramConnections('Revoke'));
      if (version === this.generation) { this.connected(reply); this.status = this.linked ? 'Could not revoke access. Try again.' : ''; }
    } catch { if (version === this.generation) this.status = 'Could not disconnect. Try again.'; }
    finally { if (version === this.generation) { this.busy = false; this.render(); } }
    if (version === this.generation && !this.linked && this.visible) await this.create();
  }
  close() {
    this.visible = false; ++this.generation; clearTimeout(this.timer); this.qrImage = ''; this.busy = false;
    const request = this.requestId; this.requestId = undefined;
    if (request) void this.cancel(request);
  }
  private async cancel(request: string) {
    try { await this.shell.processQuery(new TelegramConnections('CancelQr', request)); }
    catch { /* Expiry covers cancellation when the Desktop session has already ended. */ }
  }
  ngOnDestroy() { this.destroyed = true; window.removeEventListener('focus', this.focus); this.close(); }
}
